// Oike — pagos y aplicaciones: the rules, pure (charges.mts, the screens and the tests use them).
// Design: docs/servicios/cobros.md. A real payment is registered once (one amount, one reference,
// its payer) and spread over charges with applications. Nothing here writes; nothing is stored that
// can be computed (applied, unapplied, pending).
import { CHARGE, PAYMENT, APPLICATION } from './billingSchema.js';
import { classifyRecipient } from './personQuotes.js';
import { isAccepted } from './quoteRules.js';
import { chainsOf, totalsOf } from './quoteChains.js';
import { ChargeInputError, encodePlan, decodePlan, keptWith, isAdditional } from './charges.js';
export { encodePlan, decodePlan };

const REC_RE = /^rec[A-Za-z0-9]{14}$/;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const KEY_RE = /^[A-Za-z0-9-]{8,64}$/;
const CURRENCY_RE = /^[A-Z]{3}$/;
const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
const sel = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
const text = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
export const cents = (n) => Math.round(Number(n) * 100);
export const money = (c) => c / 100;
const fail = (code) => { throw new ChargeInputError(code); };
const validAmount = (n) => Number.isFinite(n) && n > 0 && n <= 1e9 && Math.abs(n * 100 - Math.round(n * 100)) < 1e-6;
const stamp = (now) => now.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';

// ── Who pays ──
/** The payer of a charge: { type, id } (a person's contact, or a company). */
export function payerOfCharge(f = {}) {
  return sel(f[CHARGE.payerType]) === 'company' ? { type: 'company', id: links(f[CHARGE.account])[0] || '' } : { type: 'person', id: links(f[CHARGE.stakeholder])[0] || '' };
}
export function payerOfPayment(f = {}) {
  return sel(f[PAYMENT.payerType]) === 'company' ? { type: 'company', id: links(f[PAYMENT.payerAccount])[0] || '' } : { type: 'person', id: links(f[PAYMENT.payerStakeholder])[0] || '' };
}
export const samePayer = (a, b) => !!a.id && a.type === b.type && a.id === b.id;

// ── Movements and their applications ──
/** A movement that counts (or may count, while it settles): never a voided or failed one. */
export const isLive = (p) => !['voided', 'failed'].includes(sel(p?.fields?.[PAYMENT.status]));
export const isConfirmed = (p) => sel(p?.fields?.[PAYMENT.status]) === 'confirmed';
const isRefund = (p) => sel(p?.fields?.[PAYMENT.entryType]) === 'refund';
const activeApps = (applications) => applications.filter(a => sel(a.fields?.[APPLICATION.status]) === 'active');

/**
 * What a payment (not a refund) has: its amount, what was refunded, what is applied to charges (net of
 * the refunds taken from those charges) and what is still unapplied. `includePending` counts movements
 * still settling (pending), as the server does while checking simultaneous requests.
 */
export function paymentBalance(payment, { payments = [], applications = [], includePending = false } = {}) {
  const counts = (p) => (includePending ? isLive(p) : isConfirmed(p));
  const amountC = cents(payment?.fields?.[PAYMENT.amount] || 0);
  const refunds = payments.filter(p => isRefund(p) && counts(p) && links(p.fields?.[PAYMENT.refundOf]).includes(payment.id));
  const refundedC = refunds.reduce((s, r) => s + cents(r.fields?.[PAYMENT.amount] || 0), 0);
  const act = activeApps(applications);
  const appliedC = act.filter(a => links(a.fields?.[APPLICATION.payment]).includes(payment.id)).reduce((s, a) => s + cents(a.fields?.[APPLICATION.amount] || 0), 0);
  const refundAppliedC = act.filter(a => refunds.some(r => links(a.fields?.[APPLICATION.payment]).includes(r.id))).reduce((s, a) => s + cents(a.fields?.[APPLICATION.amount] || 0), 0);
  const netAppliedC = appliedC - refundAppliedC;
  return { amount: money(amountC), refunded: money(refundedC), applied: money(netAppliedC), unapplied: money(Math.max(amountC - refundedC - netAppliedC, 0)), currency: payment?.fields?.[PAYMENT.currency] || '' };
}

/** Net applied to a charge from one payment (its applications minus the applications of its refunds). */
export function netFromPaymentToCharge(paymentId, chargeId, { payments = [], applications = [], includePending = false } = {}) {
  const counts = (p) => (includePending ? isLive(p) : isConfirmed(p));
  const refunds = payments.filter(p => isRefund(p) && counts(p) && links(p.fields?.[PAYMENT.refundOf]).includes(paymentId)).map(p => p.id);
  let c = 0;
  for (const a of activeApps(applications)) {
    if (!links(a.fields?.[APPLICATION.charge]).includes(chargeId)) continue;
    const pid = links(a.fields?.[APPLICATION.payment])[0];
    if (pid === paymentId) c += cents(a.fields?.[APPLICATION.amount] || 0);
    else if (refunds.includes(pid)) c -= cents(a.fields?.[APPLICATION.amount] || 0);
  }
  return money(c);
}

/** What a charge has received (net), counting confirmed movements (and settling ones with includePending). */
export function chargeCollected(chargeId, { payments = [], applications = [], includePending = false } = {}) {
  const byId = new Map(payments.map(p => [p.id, p]));
  let c = 0;
  for (const a of activeApps(applications)) {
    if (!links(a.fields?.[APPLICATION.charge]).includes(chargeId)) continue;
    const p = byId.get(links(a.fields?.[APPLICATION.payment])[0]);
    if (!p || !(includePending ? isLive(p) : isConfirmed(p))) continue;
    c += (isRefund(p) ? -1 : 1) * cents(a.fields?.[APPLICATION.amount] || 0);
  }
  return money(c);
}

// ── Checks ──
function checkAllocations(allocations, { payer, currency, totalC, charges, payments, applications }) {
  if (!Array.isArray(allocations)) fail('invalid_allocations');
  const seen = new Set();
  let sumC = 0;
  const out = [];
  for (const al of allocations) {
    const id = String(al?.chargeId || '');
    if (!REC_RE.test(id) || seen.has(id)) fail('invalid_allocations');
    seen.add(id);
    const amount = Number(al?.amount);
    if (!validAmount(amount)) fail('invalid_amount');
    const charge = charges.find(c => c.id === id);
    if (!charge) fail('charge_not_found');
    const f = charge.fields || {};
    if (sel(f[CHARGE.status]) !== 'open') fail('charge_cancelled');
    // A payment is never applied to another payer's charge (decision of Ale)
    if (!samePayer(payerOfCharge(f), payer)) fail('payer_mismatch');
    if (f[CHARGE.currency] !== currency) fail('currency_mismatch');
    const pendingC = cents(f[CHARGE.amount] || 0) - cents(chargeCollected(id, { payments, applications, includePending: true }));
    if (cents(amount) > pendingC) fail('over_charge');
    sumC += cents(amount);
    out.push({ chargeId: id, amount: money(cents(amount)) });
  }
  if (sumC > totalC) fail('over_payment');
  return out;
}

/**
 * Registering a payment: its fields (created as pending; confirmed by the server after its checks) and
 * the applications chosen. input: { payerType, payerStakeholderId, payerAccountId, amount, currency,
 * receivedOn, method, reference, key, allocations: [{ chargeId, amount }] }
 */
export function newPaymentPlan(input = {}, { charges = [], payments = [], applications = [], payerName = '', by = '', now = new Date() } = {}) {
  const type = input.payerType;
  if (!['person', 'company'].includes(type)) fail('payer_required');
  const id = type === 'person' ? input.payerStakeholderId : input.payerAccountId;
  if (!REC_RE.test(String(id || ''))) fail(type === 'person' ? 'person_required' : 'company_required');
  const amount = Number(input.amount);
  if (!validAmount(amount)) fail('invalid_amount');
  const currency = String(input.currency || '');
  if (!CURRENCY_RE.test(currency)) fail('invalid_currency');
  const day = String(input.receivedOn || '');
  if (!DAY_RE.test(day) || Number.isNaN(Date.parse(`${day}T12:00:00Z`))) fail('invalid_day');
  if (day > now.toISOString().slice(0, 10)) fail('future_day');
  if (!KEY_RE.test(String(input.key || ''))) fail('key_required');
  const payer = { type, id };
  const allocations = checkAllocations(input.allocations || [], { payer, currency, totalC: cents(amount), charges, payments, applications });
  const fields = {
    [PAYMENT.name]: `Pago · ${text(payerName, 120) || (type === 'person' ? 'Persona' : 'Empresa')} · ${day}`,
    [PAYMENT.entryType]: 'payment', [PAYMENT.amount]: money(cents(amount)), [PAYMENT.currency]: currency, [PAYMENT.receivedOn]: day,
    [PAYMENT.method]: text(input.method, 80), [PAYMENT.reference]: text(input.reference, 120), [PAYMENT.channel]: 'manual',
    [PAYMENT.status]: 'pending', [PAYMENT.payerType]: type, [PAYMENT.recordedBy]: text(by, 200), [PAYMENT.key]: input.key,
    [PAYMENT.log]: `${stamp(now)} · ${text(by, 200)}: pago registrado.`,
  };
  fields[type === 'person' ? PAYMENT.payerStakeholder : PAYMENT.payerAccount] = [id];
  return { fields, allocations };
}

/** Applying (more of) a confirmed payment's unapplied money. */
export function newApplications(payment, allocations, { charges = [], payments = [], applications = [] } = {}) {
  if (!isConfirmed(payment) || isRefund(payment)) fail('payment_not_confirmed');
  const bal = paymentBalance(payment, { payments, applications, includePending: true });
  return checkAllocations(allocations, { payer: payerOfPayment(payment.fields || {}), currency: payment.fields?.[PAYMENT.currency], totalC: cents(bal.unapplied), charges, payments, applications });
}

/** The fields of one application. */
export function applicationFields(paymentId, al, { key, by = '', now = new Date(), currency, label = '' }) {
  return {
    [APPLICATION.name]: `${label || 'Aplicación'} · ${money(cents(al.amount))} ${currency}`,
    [APPLICATION.payment]: [paymentId], [APPLICATION.charge]: [al.chargeId], [APPLICATION.amount]: money(cents(al.amount)),
    [APPLICATION.currency]: currency, [APPLICATION.status]: 'active', [APPLICATION.appliedAt]: now.toISOString(),
    [APPLICATION.appliedBy]: text(by, 200), [APPLICATION.key]: key, [APPLICATION.log]: `${stamp(now)} · ${text(by, 200)}: aplicado.`,
  };
}

/**
 * A refund of a confirmed payment: one movement (refund, Refund Of) and, if it comes from charges,
 * its applications to those charges. The original payment, its applications and its receipt stay.
 * input: { amount, receivedOn, method, reference, reason, key, fromCharges: [{ chargeId, amount }] }
 */
export function newRefundPlan(original, input = {}, { payments = [], applications = [], by = '', now = new Date() } = {}) {
  if (!isConfirmed(original) || isRefund(original)) fail('payment_not_confirmed');
  const amount = Number(input.amount);
  if (!validAmount(amount)) fail('invalid_amount');
  const day = String(input.receivedOn || '');
  if (!DAY_RE.test(day) || Number.isNaN(Date.parse(`${day}T12:00:00Z`))) fail('invalid_day');
  if (day > now.toISOString().slice(0, 10)) fail('future_day');
  const reason = text(input.reason, 500);
  if (reason.length < 10) fail('reason_required');
  if (input.confirm !== true) fail('confirm_required');
  if (!KEY_RE.test(String(input.key || ''))) fail('key_required');
  const bal = paymentBalance(original, { payments, applications, includePending: true });
  if (cents(amount) > cents(bal.amount) - cents(bal.refunded)) fail('over_refund');
  const from = Array.isArray(input.fromCharges) ? input.fromCharges : [];
  const seen = new Set(); let fromC = 0;
  for (const fc of from) {
    if (!REC_RE.test(String(fc?.chargeId || '')) || seen.has(fc.chargeId) || !validAmount(Number(fc.amount))) fail('invalid_allocations');
    seen.add(fc.chargeId);
    if (cents(fc.amount) > cents(netFromPaymentToCharge(original.id, fc.chargeId, { payments, applications, includePending: true }))) fail('over_refund_charge');
    fromC += cents(fc.amount);
  }
  if (fromC > cents(amount)) fail('invalid_allocations');
  // The part not taken from charges comes out of the unapplied money
  if (cents(amount) - fromC > cents(bal.unapplied)) fail('refund_needs_charges');
  const of = original.fields || {};
  const payer = payerOfPayment(of);
  const fields = {
    [PAYMENT.name]: `Devolución · ${of[PAYMENT.name] || ''}`.slice(0, 200), [PAYMENT.entryType]: 'refund', [PAYMENT.refundOf]: [original.id],
    [PAYMENT.amount]: money(cents(amount)), [PAYMENT.currency]: of[PAYMENT.currency], [PAYMENT.receivedOn]: day,
    [PAYMENT.method]: text(input.method, 80), [PAYMENT.reference]: text(input.reference, 120), [PAYMENT.channel]: 'manual',
    [PAYMENT.status]: 'pending', [PAYMENT.payerType]: payer.type, [PAYMENT.recordedBy]: text(by, 200), [PAYMENT.key]: input.key,
    [PAYMENT.log]: `${stamp(now)} · ${text(by, 200)}: devolución registrada. Motivo: ${reason}`,
  };
  fields[payer.type === 'person' ? PAYMENT.payerStakeholder : PAYMENT.payerAccount] = [payer.id];
  return { fields, fromCharges: from.map(fc => ({ chargeId: fc.chargeId, amount: money(cents(fc.amount)) })) };
}

/**
 * The server's check after writing, with everything written (also by simultaneous requests): which
 * applications go over a limit and must be reversed (the newest first). Limits: a charge never gets more
 * than its amount; a payment never applies more than it has. Counts settling movements too.
 */
export function overLimitApplications({ charges = [], payments = [], applications = [] }) {
  const byPay = new Map(payments.map(p => [p.id, p]));
  const live = activeApps(applications).filter(a => { const p = byPay.get(links(a.fields?.[APPLICATION.payment])[0]); return p && isLive(p); });
  const amt = (a) => cents(a.fields?.[APPLICATION.amount] || 0);
  const payOf = (a) => byPay.get(links(a.fields?.[APPLICATION.payment])[0]);
  const chargeOf = (a) => links(a.fields?.[APPLICATION.charge])[0];
  // Refund applications give room back to their charge
  const refundRoom = new Map();
  for (const a of live) if (isRefund(payOf(a))) refundRoom.set(chargeOf(a), (refundRoom.get(chargeOf(a)) || 0) + amt(a));
  // A payment's room: its amount minus what its refunds took from the unapplied money
  const payRoom = (p) => {
    const refunds = payments.filter(r => isRefund(r) && isLive(r) && links(r.fields?.[PAYMENT.refundOf]).includes(p.id));
    const refundedC = refunds.reduce((s, r) => s + cents(r.fields?.[PAYMENT.amount] || 0), 0);
    const fromChargesC = live.filter(a => refunds.some(r => r.id === payOf(a).id)).reduce((s, a) => s + amt(a), 0);
    return cents(p.fields?.[PAYMENT.amount] || 0) - (refundedC - fromChargesC);
  };
  const ordered = live.filter(a => !isRefund(payOf(a)))
    .sort((a, b) => String(a.createdTime || '').localeCompare(String(b.createdTime || '')) || a.id.localeCompare(b.id));
  const reverse = [];
  const usedCharge = new Map(); const usedPay = new Map();
  for (const a of ordered) {
    const cid = chargeOf(a); const pay = payOf(a);
    const charge = charges.find(c => c.id === cid);
    const uc = (usedCharge.get(cid) || 0) + amt(a); const up = (usedPay.get(pay.id) || 0) + amt(a);
    if (!charge || uc > cents(charge.fields?.[CHARGE.amount] || 0) + (refundRoom.get(cid) || 0) || up > payRoom(pay)) { reverse.push(a.id); continue; }
    usedCharge.set(cid, uc); usedPay.set(pay.id, up);
  }
  return reverse;
}

// ── Plans of installments ──
/** Plans with installments missing (a creation that failed half-way): plan key, total, missing numbers. */
export function incompletePlans(charges = []) {
  const groups = new Map();
  for (const c of charges) {
    const k = c.fields?.[CHARGE.planKey];
    if (!k) continue;
    groups.set(k, [...(groups.get(k) || []), c]);
  }
  const out = [];
  for (const [planKey, list] of groups) {
    const total = Number(list[0].fields?.[CHARGE.installmentsTotal]) || 0;
    const have = new Set(list.map(c => Number(c.fields?.[CHARGE.installmentNumber])));
    const missing = Array.from({ length: total }, (_, i) => i + 1).filter(n => !have.has(n));
    if (missing.length) out.push({ planKey, total, missing, sample: list[0], definition: list.map(c => decodePlan(c.fields?.[CHARGE.log])).find(Boolean) || null });
  }
  return out;
}

// ── Accepted proposals and what is receivable ──
/**
 * The accepted proposals of a payer (accepted by the client: Quote Status = Won; an internal approval is
 * not enough), each with its receivable: the agreed one-off total, the charges of the accepted version,
 * what was applied to them and what is pending, per currency; and what earlier versions already
 * collected (never copied, never asked again: shown to deduct when generating the plan).
 */
export function acceptedProposalsOf(payer, { intakes = [], charges = [], payments = [], applications = [] } = {}) {
  const out = [];
  for (const chain of chainsOf(intakes)) {
    const sale = chain.sale;
    if (!sale || !isAccepted(sale.record.fields || {})) continue;
    const f = sale.record.fields || {};
    const r = classifyRecipient(f);
    const qPayer = r.kind === 'personal' ? { type: 'person', id: links(f['Stakeholder'])[0] || '' } : r.kind === 'company' ? { type: 'company', id: links(f['Account'])[0] || '' } : null;
    if (!qPayer || !samePayer(qPayer, payer)) continue;
    const totals = totalsOf(f);
    const versionCharges = charges.filter(c => links(c.fields?.[CHARGE.quote]).includes(sale.id));
    // The version's plan (an independent additional contract is apart: it never hides a duplicated debt)
    const openAll = versionCharges.filter(c => sel(c.fields?.[CHARGE.status]) === 'open');
    const open = openAll.filter(c => !isAdditional(c.fields || {}));
    const additional = openAll.filter(c => isAdditional(c.fields || {}));
    const plannedC = open.reduce((s, c) => s + cents(c.fields?.[CHARGE.amount] || 0), 0);
    const collectedC = open.reduce((s, c) => s + cents(chargeCollected(c.id, { payments, applications })), 0);
    const additionalPendingC = additional.reduce((s, c) => s + Math.max(cents(c.fields?.[CHARGE.amount] || 0) - cents(chargeCollected(c.id, { payments, applications })), 0), 0);
    const earlier = chain.versions.filter(v => v.id !== sale.id && chain.versions.indexOf(v) < chain.versions.indexOf(sale));
    const earlierCollectedC = charges.filter(c => earlier.some(v => links(c.fields?.[CHARGE.quote]).includes(v.id)) && c.fields?.[CHARGE.currency] === totals.currency)
      .reduce((s, c) => s + cents(chargeCollected(c.id, { payments, applications })), 0);
    const earlierLegacy = earlier.filter(v => v.payment.state !== 'none').map(v => v.number);
    const earlierOpen = charges.filter(c => earlier.some(v => links(c.fields?.[CHARGE.quote]).includes(v.id)) && sel(c.fields?.[CHARGE.status]) === 'open' && cents(chargeCollected(c.id, { payments, applications })) < cents(c.fields?.[CHARGE.amount] || 0));
    // Earlier charges still pending: each one must be resolved by Ale (kept with this version, or cancelled) before a payable total is shown
    const kept = earlierOpen.filter(c => keptWith(c.fields || {}, sale.id));
    const unresolved = earlierOpen.filter(c => !keptWith(c.fields || {}, sale.id));
    const keptPendingC = kept.filter(c => c.fields?.[CHARGE.currency] === totals.currency).reduce((s, c) => s + cents(c.fields?.[CHARGE.amount] || 0) - cents(chargeCollected(c.id, { payments, applications })), 0);
    // A revision below what was already paid: the difference is shown to resolve; never a negative amount nor an automatic refund
    const aboveAgreedC = Math.max(earlierCollectedC - cents(totals.once), 0);
    out.push({
      chainId: chain.id, quote: sale.record, number: sale.number, currency: totals.currency,
      agreed: totals.once, monthly: totals.monthly, hasPlan: versionCharges.length > 0,
      planned: money(plannedC), collected: money(collectedC), pending: money(Math.max(plannedC - collectedC, 0)),
      // Still to plan: agreed − collected in earlier versions − what earlier charges kept with this version still owe − this version's plan.
      // E.g. 1.200 agreed, 200 collected in v1 and a v1 installment of 300 kept → 700, never 1.000 again.
      notPlanned: money(Math.max(cents(totals.once) - earlierCollectedC - keptPendingC - plannedC, 0)),
      overPlanned: money(Math.max(earlierCollectedC + keptPendingC + plannedC - cents(totals.once), 0)),
      keptPending: money(keptPendingC),
      additionalCharges: additional.map(c => c.id), additionalPending: money(additionalPendingC),
      earlierCollected: money(earlierCollectedC), earlierLegacy, earlierOpenCharges: earlierOpen.map(c => c.id),
      // This version had a payment status before charges existed (no amount): never turned into money, pointed out to check
      legacyPayment: sale.payment.state !== 'none' ? sale.payment.state : null,
      unresolvedEarlier: unresolved.map(c => c.id), keptEarlier: kept.map(c => c.id),
      paidAboveAgreed: money(aboveAgreedC),
      // What is payable now: only when nothing is left to resolve (this version's plan + earlier charges kept with it)
      payable: unresolved.length || aboveAgreedC > 0 ? null : money(Math.max(plannedC - collectedC, 0) + keptPendingC),
      canPlan: !unresolved.length && aboveAgreedC === 0,
      charges: versionCharges,
    });
  }
  return out;
}

/**
 * Why a new plan for this accepted version cannot be created yet (or null): earlier versions with
 * pending charges that Ale has not resolved, or earlier payments above this version's total.
 */
export function quotePlanBlock(quoteId, { intakes = [], charges = [], payments = [], applications = [] } = {}) {
  const chain = chainsOf(intakes).find(c => c.versions.some(v => v.id === quoteId));
  if (!chain) return null;
  const idx = chain.versions.findIndex(v => v.id === quoteId);
  const earlier = chain.versions.slice(0, idx);
  const target = chain.versions[idx];
  const earlierCharges = charges.filter(c => earlier.some(v => links(c.fields?.[CHARGE.quote]).includes(v.id)));
  const unresolved = earlierCharges.filter(c => sel(c.fields?.[CHARGE.status]) === 'open' && !keptWith(c.fields || {}, quoteId)
    && cents(chargeCollected(c.id, { payments, applications })) < cents(c.fields?.[CHARGE.amount] || 0));
  if (unresolved.length) return 'earlier_plan_unresolved';
  const currency = totalsOf(target.record.fields || {}).currency;
  const collectedC = earlierCharges.filter(c => c.fields?.[CHARGE.currency] === currency).reduce((s, c) => s + cents(chargeCollected(c.id, { payments, applications })), 0);
  if (collectedC > cents(totalsOf(target.record.fields || {}).once)) return 'paid_above_agreed';
  return null;
}

/** Whether a charge belongs to an earlier version of the chain of this (later) version. */
export function isEarlierChargeOf(charge, quoteId, intakes = []) {
  const chain = chainsOf(intakes).find(c => c.versions.some(v => v.id === quoteId));
  if (!chain) return false;
  const idx = chain.versions.findIndex(v => v.id === quoteId);
  return chain.versions.slice(0, idx).some(v => links(charge?.fields?.[CHARGE.quote]).includes(v.id));
}

/** The accepted-proposal entry (acceptedProposalsOf) of one version, whoever its payer is; null if not accepted or not certain. */
export function proposalOfVersion(quoteId, ctx = {}) {
  const q = (ctx.intakes || []).find(r => r.id === quoteId);
  if (!q) return null;
  const r = classifyRecipient(q.fields || {});
  const payer = r.kind === 'personal' ? { type: 'person', id: links(q.fields?.['Stakeholder'])[0] || '' } : r.kind === 'company' ? { type: 'company', id: links(q.fields?.['Account'])[0] || '' } : null;
  return payer ? acceptedProposalsOf(payer, ctx).find(p => p.quote.id === quoteId) || null : null;
}
